import { useQueryClient } from "@tanstack/react-query";
import { Building } from "lucide-react";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { Button } from "@krea/shared/design-system/Button";
import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import { FieldDescription } from "@krea/shared/design-system/input/field";
import { Input } from "@krea/shared/design-system/input/input";
import { Label } from "@krea/shared/design-system/Label";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import { Separator } from "@krea/shared/design-system/Separator";
import { toast } from "@krea/shared/design-system/utils/toast";

import { queryKeyMap } from "@/features/query-client";

import { useGetCurrentLenderCompanyQuery } from "../hooks/use-get-lender-company.query";
import { useUpdateLenderCompanyMutation } from "../hooks/use-update-lender-company.mutation";
import { LenderProfileSectionCard } from "./lender-profile-section-card";

const schema = yup.object().shape({
  name: yup.string().required("Company name is required"),
  customerContactPhoneNumber: yup.string().nonNullable(),
  customerContactEmail: yup.string().email("Invalid email").nonNullable(),
});

type IFormValues = yup.InferType<typeof schema>;

export const LenderProfileDetailsSection = () => {
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const queryClient = useQueryClient();

  const form = useForm<IFormValues>({
    mode: "onChange",
    defaultValues: {
      name: lenderCompany?.name ?? "",
      customerContactPhoneNumber:
        lenderCompany?.customerContactPhoneNumber ?? "",
      customerContactEmail: lenderCompany?.customerContactEmail ?? "",
    },
  });
  const { mutate: updateLenderCompany } = useUpdateLenderCompanyMutation();

  useEffect(() => {
    if (lenderCompany) {
      const initialValues: IFormValues = {
        name: lenderCompany.name,
        customerContactPhoneNumber:
          lenderCompany.customerContactPhoneNumber ?? "",
        customerContactEmail: lenderCompany.customerContactEmail ?? "",
      };
      form.reset(initialValues);
    }
  }, [lenderCompany, form]);

  const handleSubmit = useCallback(() => {
    if (!lenderCompany) return;
    form.handleSubmit((data) => {
      updateLenderCompany(
        { lenderCompanyId: lenderCompany.id, data },
        {
          onSuccess: (data) => {
            queryClient.setQueryData(
              queryKeyMap.getLenderCompany(lenderCompany.id),
              data,
            );
            toast.success("Company profile updated successfully");
          },
          onError: () => {
            toast.error("Failed to update company profile");
          },
        },
      );
    })();
  }, [lenderCompany, form, updateLenderCompany, queryClient]);

  return (
    <LenderProfileSectionCard
      title="Company Overview"
      description="Basic information about the lending company"
    >
      <div className="flex flex-col items-start gap-8 md:flex-row">
        <div className="flex flex-col items-center gap-3">
          {lenderCompany?.imageUrl ? (
            <img
              src={lenderCompany.imageUrl}
              alt="Company Logo"
              className="h-32 w-32 rounded-xl object-contain"
            />
          ) : (
            <div className="flex h-32 w-32 items-center justify-center rounded-xl bg-primary ring-4 ring-gray-100">
              <Building className="h-10 w-10 text-white" />
            </div>
          )}
          <ResponsiveTooltip
            trigger={
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-primary"
              >
                Change Company Logo
              </Button>
            }
            interactive
          >
            To change the company logo, please contact customer support.
          </ResponsiveTooltip>
        </div>
        <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-5">
          <div className="col-span-2">
            <ControlledWrapper
              control={form.control}
              name="name"
              label="Company Name"
              render={({ field }) => (
                <>
                  <Input
                    type="text"
                    placeholder="Company Name"
                    {...field}
                    disabled={form.formState.isSubmitting}
                  />
                  <FieldDescription>
                    The name of the company that will be displayed to the
                    customers.
                  </FieldDescription>
                </>
              )}
            />
          </div>

          <div className="col-span-2">
            <div className="space-y-2">
              <Label>Description</Label>
              <div
                // typography: rich-HTML description container uses text-base (matches text-base-long) but font-medium for slightly stronger emphasis on lender's public-facing description
                className="rounded-md border border-gray-200 px-4 py-2 text-base font-medium text-black shadow-md"
                dangerouslySetInnerHTML={{
                  __html: lenderCompany?.description ?? "",
                }}
              />
              <p className="text-helper">
                The description of the company that will be displayed to the
                customers. Contact customer support to update this information.
              </p>
            </div>
          </div>

          <div className="col-span-2">
            <ControlledWrapper
              control={form.control}
              name="customerContactPhoneNumber"
              label="Contact Phone Number"
              render={({ field }) => (
                <>
                  <Input
                    type="text"
                    placeholder="Contact Phone Number"
                    {...field}
                    disabled={form.formState.isSubmitting}
                  />
                  <FieldDescription>
                    The phone number of the company that will be displayed to
                    the customers when they contact the company after selecting
                    the lender&apos;s bid.
                  </FieldDescription>
                </>
              )}
            />
          </div>

          <div className="col-span-2">
            <ControlledWrapper
              control={form.control}
              name="customerContactEmail"
              label="Contact Email"
              render={({ field }) => (
                <>
                  <Input
                    type="email"
                    placeholder="Contact Email"
                    {...field}
                    disabled={form.formState.isSubmitting}
                  />
                  <FieldDescription>
                    The email of the company that will be displayed to the
                    customers when they contact the company after selecting the
                    lender&apos;s bid.
                  </FieldDescription>
                </>
              )}
            />
          </div>
          <div className="col-span-2">
            <Separator />
          </div>
          <div className="col-span-2 flex justify-end">
            <Button
              disabled={
                form.formState.isSubmitting ||
                !form.formState.isValid ||
                !form.formState.isDirty
              }
              size="lg"
              onClick={handleSubmit}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </LenderProfileSectionCard>
  );
};
